import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Package, Clock, Video, Repeat, Star, Filter, Search, ChevronRight } from 'lucide-react'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import PublicHeader from '../components/PublicHeader'

const NICHE_OPTIONS = [
  { value: '', label: 'Alle niches' },
  { value: 'beauty', label: 'Beauty' },
  { value: 'fashion', label: 'Fashion' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'food', label: 'Food' },
  { value: 'tech', label: 'Tech' },
  { value: 'travel', label: 'Travel' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'parenting', label: 'Parenting' },
  { value: 'pets', label: 'Pets' },
  { value: 'home', label: 'Home' }
]

const SORT_OPTIONS = [
  { value: 'recent', label: 'Meest recent' },
  { value: 'price_asc', label: 'Prijs: laag → hoog' },
  { value: 'price_desc', label: 'Prijs: hoog → laag' },
  { value: 'popular', label: 'Populair' }
]

export default function PackagesMarketplacePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [packages, setPackages] = useState([])
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 })

  const filters = useMemo(() => ({
    niche: searchParams.get('niche') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    maxDeliveryDays: searchParams.get('maxDeliveryDays') || '',
    q: searchParams.get('q') || '',
    sort: searchParams.get('sort') || 'recent',
    page: parseInt(searchParams.get('page') || '1', 10)
  }), [searchParams])

  useEffect(() => {
    let cancelled = false
    const fetchPackages = async () => {
      setLoading(true)
      try {
        const params = {}
        Object.entries(filters).forEach(([k, v]) => {
          if (v !== '' && v !== null && v !== undefined) params[k] = v
        })
        const resp = await api.get('/packages', { params })
        if (cancelled) return
        setPackages(resp.data.packages || [])
        setPagination(resp.data.pagination || { page: 1, total: 0, pages: 1 })
      } catch (err) {
        console.error('Error loading packages:', err)
        if (!cancelled) setPackages([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchPackages()
    return () => { cancelled = true }
  }, [filters])

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams)
    if (value === '' || value === null || value === undefined) {
      newParams.delete(key)
    } else {
      newParams.set(key, value)
    }
    if (key !== 'page') newParams.delete('page') // filter change resets paging
    setSearchParams(newParams)
  }

  const handleBookPackage = (pkg) => {
    if (!user) {
      navigate('/login?redirect=/pakketten', { state: { from: { pathname: '/pakketten' } } })
      return
    }
    if (user.role !== 'bedrijf' && user.role !== 'agency') {
      alert('Alleen bedrijven kunnen pakketten boeken.')
      return
    }
    navigate(`/checkout/${pkg.creator.id}/${pkg.packageIndex}`)
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicHeader />

      {/* Titelbalk */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">UGC Pakketten</h1>
          <p className="text-[#475569] max-w-2xl">
            Vaste prijzen, vaste deliverables. Kies een pakket, betaal direct en de creator gaat aan de slag — geen onderhandeling nodig.
          </p>
        </div>
      </div>

      {/* Filters bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Zoek pakket of creator..."
                value={filters.q}
                onChange={(e) => updateFilter('q', e.target.value)}
                className="w-full min-w-0 min-h-[44px] pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-base md:text-sm focus:outline-none focus:border-[#3EB489]"
              />
            </div>
            <select
              value={filters.niche}
              onChange={(e) => updateFilter('niche', e.target.value)}
              className="w-full min-w-0 min-h-[44px] px-3 py-2.5 border border-gray-200 rounded-xl text-base md:text-sm bg-white focus:outline-none focus:border-[#3EB489]"
            >
              {NICHE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <input
              type="number"
              placeholder="Max prijs (€)"
              value={filters.maxPrice}
              onChange={(e) => updateFilter('maxPrice', e.target.value)}
              className="w-full min-w-0 min-h-[44px] px-3 py-2.5 border border-gray-200 rounded-xl text-base md:text-sm focus:outline-none focus:border-[#3EB489]"
            />
            <select
              value={filters.sort}
              onChange={(e) => updateFilter('sort', e.target.value)}
              className="w-full min-w-0 min-h-[44px] px-3 py-2.5 border border-gray-200 rounded-xl text-base md:text-sm bg-white focus:outline-none focus:border-[#3EB489]"
            >
              {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>

        {/* Results count */}
        <div className="flex justify-between items-center mt-6 mb-4">
          <p className="text-sm text-[#475569]">
            {loading ? 'Laden...' : `${pagination.total} ${pagination.total === 1 ? 'pakket' : 'pakketten'} gevonden`}
          </p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white border border-gray-200 rounded-2xl p-5 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-6"></div>
                <div className="h-20 bg-gray-100 rounded mb-4"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : packages.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-[#475569] mb-1">Geen pakketten gevonden</p>
            <p className="text-sm text-gray-400">Probeer je filters aan te passen.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {packages.map((pkg, i) => (
              <div key={`${pkg.creator.id}-${pkg.packageIndex}`} className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md hover:border-[#3EB489] transition flex flex-col">
                {/* Creator header */}
                <Link to={`/creators/${pkg.creator.id}`} className="flex items-center gap-3 mb-4 group">
                  {pkg.creator.profilePictureUrl ? (
                    <img src={pkg.creator.profilePictureUrl} alt={pkg.creator.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center text-sm font-semibold text-blue-700">
                      {pkg.creator.name.charAt(0)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0F172A] group-hover:text-[#3EB489] transition truncate">{pkg.creator.name}</p>
                    {pkg.creator.rating && (
                      <div className="flex items-center gap-1 text-xs text-[#475569]">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        <span>{pkg.creator.rating.toFixed(1)}</span>
                        <span>·</span>
                        <span>{pkg.creator.completedProjects} projecten</span>
                      </div>
                    )}
                  </div>
                </Link>

                {/* Package details */}
                <div className="flex-1 mb-4">
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">{pkg.name}</h3>
                  {pkg.description && (
                    <p className="text-sm text-[#475569] mb-3 line-clamp-2">{pkg.description}</p>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-xs text-[#475569] mb-3">
                    <div className="flex items-center gap-1.5">
                      <Video className="w-3.5 h-3.5 text-[#3EB489]" />
                      <span>{pkg.videoCount} × {pkg.videoLength}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-[#3EB489]" />
                      <span>{pkg.deliveryDays}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Repeat className="w-3.5 h-3.5 text-[#3EB489]" />
                      <span>{pkg.revisions} revisie{pkg.revisions === 1 ? '' : 's'}</span>
                    </div>
                    {pkg.hooks > 0 && (
                      <div className="flex items-center gap-1.5">
                        <Package className="w-3.5 h-3.5 text-[#3EB489]" />
                        <span>{pkg.hooks} hook{pkg.hooks === 1 ? '' : 's'}</span>
                      </div>
                    )}
                  </div>

                  {pkg.includedItems && pkg.includedItems.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {pkg.includedItems.slice(0, 4).map(item => (
                        <span key={item} className="text-[11px] uppercase tracking-wide px-2 py-0.5 bg-white border border-[#3EB489]/40 text-[#2A7E5F] rounded-md">
                          {item.replace(/_/g, ' ')}
                        </span>
                      ))}
                      {pkg.includedItems.length > 4 && (
                        <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 bg-white border border-gray-200 text-gray-500 rounded-md">
                          +{pkg.includedItems.length - 4} meer
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Price + CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-[#475569]">Vanaf</p>
                    <p className="text-2xl font-bold text-[#0F172A]">€{pkg.price}</p>
                  </div>
                  <button
                    onClick={() => handleBookPackage(pkg)}
                    className="inline-flex items-center gap-1 min-h-[44px] px-4 py-2.5 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition"
                  >
                    Bestel
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <nav className="mt-8 flex flex-col items-center gap-3" aria-label="Paginering">
            <div className="flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => updateFilter('page', Math.max(1, pagination.page - 1))}
                disabled={pagination.page <= 1}
                className="min-w-[44px] h-11 px-3 rounded-xl text-sm font-semibold bg-white border border-gray-200 text-[#0F172A] hover:border-[#3EB489] disabled:opacity-40 disabled:cursor-not-allowed transition"
                aria-label="Vorige pagina"
              >
                ‹
              </button>
              {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                .filter(p => pagination.pages <= 7 || p === 1 || p === pagination.pages || Math.abs(p - pagination.page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…')
                  acc.push(p)
                  return acc
                }, [])
                .map((p, idx) => p === '…' ? (
                  <span key={`gap-${idx}`} className="min-w-[24px] h-11 inline-flex items-center justify-center text-sm text-[#94A3B8]">…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => updateFilter('page', p)}
                    aria-current={p === pagination.page ? 'page' : undefined}
                    className={`min-w-[44px] h-11 px-3 rounded-xl text-sm font-semibold transition ${
                      p === pagination.page ? 'bg-[#3EB489] text-white' : 'bg-white border border-gray-200 text-[#0F172A] hover:border-[#3EB489]'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              <button
                type="button"
                onClick={() => updateFilter('page', Math.min(pagination.pages, pagination.page + 1))}
                disabled={pagination.page >= pagination.pages}
                className="min-w-[44px] h-11 px-3 rounded-xl text-sm font-semibold bg-white border border-gray-200 text-[#0F172A] hover:border-[#3EB489] disabled:opacity-40 disabled:cursor-not-allowed transition"
                aria-label="Volgende pagina"
              >
                ›
              </button>
            </div>
            <p className="text-xs text-[#64748B]">Pagina {pagination.page} van {pagination.pages}</p>
          </nav>
        )}
      </div>
    </div>
  )
}
